import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";

const fixLeafletIcons = () => {
  // eslint-disable-next-line no-underscore-dangle
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  });
};

const userLabel = (marker) => {
  const u = marker.user;
  if (u?.id) {
    return [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || `User #${u.id}`;
  }
  return marker.city || marker.country || marker.distinct_id?.slice(0, 12) || "User";
};

const WorldUserMap = ({ markers = [], onSelectUsers, onSelectUser }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const onSelectUsersRef = useRef(onSelectUsers);
  const onSelectUserRef = useRef(onSelectUser);

  onSelectUsersRef.current = onSelectUsers;
  onSelectUserRef.current = onSelectUser;

  useEffect(() => {
    fixLeafletIcons();
    if (!containerRef.current || mapRef.current) return undefined;

    const map = L.map(containerRef.current, {
      center: [20, 0],
      zoom: 2,
      minZoom: 2,
      maxZoom: 12,
      worldCopyJump: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>',
      subdomains: "abc",
      maxZoom: 19,
    }).addTo(map);

    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: false,
      zoomToBoundsOnClick: false,
      maxClusterRadius: 55,
      iconCreateFunction: (c) => {
        const count = c.getChildCount();
        let size = "small";
        if (count >= 100) size = "large";
        else if (count >= 10) size = "medium";
        return L.divIcon({
          html: `<div><span>${count}</span></div>`,
          className: `analytics-map-cluster analytics-map-cluster--${size}`,
          iconSize: L.point(44, 44),
        });
      },
    });

    cluster.on("clusterclick", (e) => {
      L.DomEvent.stopPropagation(e);
      const childMarkers = e.layer.getAllChildMarkers();
      const users = childMarkers.map((m) => m.options.userData).filter(Boolean);
      if (users.length === 1) {
        onSelectUserRef.current?.(users[0]);
      } else {
        onSelectUsersRef.current?.(users, e.layer.getChildCount());
      }
    });

    map.addLayer(cluster);
    mapRef.current = map;
    clusterRef.current = cluster;

    return () => {
      map.remove();
      mapRef.current = null;
      clusterRef.current = null;
    };
  }, []);

  useEffect(() => {
    const cluster = clusterRef.current;
    const map = mapRef.current;
    if (!cluster || !map) return;

    cluster.clearLayers();

    const valid = (markers || []).filter((m) => m.lat && m.lng);
    valid.forEach((marker) => {
      const lm = L.marker([marker.lat, marker.lng], { userData: marker });
      const label = userLabel(marker);
      const loc = [marker.city, marker.country].filter(Boolean).join(", ");
      lm.bindTooltip(label, { direction: "top", offset: [0, -8] });
      lm.bindPopup(
        `<strong>${label}</strong><br/>${loc || "Unknown location"}<br/>Events: ${marker.events_count ?? 0}`
      );
      lm.on("click", (ev) => {
        L.DomEvent.stopPropagation(ev);
        onSelectUserRef.current?.(marker);
      });
      cluster.addLayer(lm);
    });

    if (valid.length) {
      const bounds = L.latLngBounds(valid.map((m) => [m.lat, m.lng]));
      if (bounds.isValid()) {
        map.fitBounds(bounds.pad(0.15), { maxZoom: 6 });
      }
    }
  }, [markers]);

  return <div ref={containerRef} className="analytics-world-map" aria-label="World user map" />;
};

export default WorldUserMap;
